<template>
    <div class="modal modal-xl" id="readingsModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog">
            <div class="modal-content">
                <div class="modal-header">
                    <h4 class="modal-title">Avläsningar</h4>
                    <button type="button" class="btn-close" aria-label="Close" @click="$emit('close')"></button>
                </div>
                <div class="modal-body">
                    <div class="row">
                        <div class="col">
                            <label for="apartmentSelect" class="form-label">Mätplats</label>
                            <select id="apartmentSelect" class="form-select" @change="$emit('change-apartment')" v-model="data.apartmentId"
                                :disabled="apartments.length === 0">
                                <option v-for="apartment in apartments" :key="apartment.id" :value="apartment.id">
                                    {{ apartment.number }}
                                </option>
                            </select>
                        </div>
                        <div class="col">
                            <label for="deviceSelect" class="form-label">Mätare</label>
                            <select id="deviceSelect" class="form-select" @change="$emit('update', data.measurement)" v-model="data.deviceId"
                                :disabled="data.devices.length === 0">
                                <option v-for="d in data.devices" :key="d.deviceId" :value="d.deviceId">
                                    {{ seriesHelpers.getMeasurementLabel(d.measurement) + ' ' + d.idNumber }}
                                </option>
                            </select>
                        </div>
                    </div>
                    <div class="row mt-3">
                        <div class="col-5">
                            <label for="startInput" class="form-label">Från</label>
                            <VueDatePicker id="startInput" v-model="data.start" locale="sv" format="yyyy-MM-dd HH:mm" select-text="Välj" cancel-text="Avbryt" />
                        </div>
                        <div class="col-5">
                            <label for="stopInput" class="form-label">Till</label>
                            <VueDatePicker id="stopInput" v-model="data.stop" locale="sv" format="yyyy-MM-dd HH:mm" select-text="Välj" cancel-text="Avbryt" />
                        </div>
                        <div class="col-2">
                            <div>&nbsp;</div>
                            <button class="btn btn-primary mt-2" @click="$emit('update')">Ladda om</button>
                            &nbsp;
                            <button class="btn btn-secondary mt-2" @click="download" title="Ladda ner data"><i class="bi bi-download"></i></button>
                        </div>
                    </div>
                    <div v-if="user.level <= 10" class="row mt-3">
                        <div class="col-4">
                            <div v-if="!showReadingEditor">
                                <span v-on:click="showReadingEditor = true"><i class="bi bi-plus-square"></i> Lägg till/redigera mätvärde</span>
                            </div>
                            <div v-else>
                                <span v-on:click="showReadingEditor = false"><i class="bi bi-dash-square"></i> Lägg till/redigera mätvärde</span>
                            </div>
                        </div>
                        <div class="col-8" v-if="showReadingEditor">
                            <div class="card bg-light mt-3">
                                <div class="card-body">
                                    <div class="row">
                                        <div class="col-2"><span v-on:dblclick="setTimestamp">Tidpunkt</span></div>
                                        <div class="col-8"><input id="timestampInput" type="text" class="form-control" placeholder="åååå-MM-dd tt:mm" :value="formReading ? new Date(formReading.timestamp).toLocaleDateString('sv', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 }).replace(',', '.') : ''" :disabled="formReading !== null"></div>
                                        <div class="col-2"><i class="bi bi-info-circle" title="Tidpunkten kan anges i följande format: åååå-MM-dd tt:mm, åååå-MM-dd tt:mm:ss eller åååå-MM-dd tt:mm:ss.mmm. Där MM är månad, mm är minuter och mmm är millisekunder."></i>
                                        </div>
                                    </div>
                                    <div class="row mt-1" v-for="field in data.mpFields" :key="field">
                                        <div class="col-2">{{ seriesHelpers.getFieldShortLabel(field) }}</div>
                                        <div class="col-8"><input :id="field + 'FieldInput'" type="text" class="form-control" :value="formReading?.fields[field] ? formReading.fields[field].toLocaleString('sv', { useGrouping: false }) : ''" /></div>
                                        <div class="col-2">{{ seriesHelpers.getFieldUnit(field) }}</div>
                                    </div>
                                    <div class="mt-3 text-end">
                                        <button class="btn btn-outline-danger me-2" v-if="formReading" v-on:click="removeReading">Ta bort</button>
                                        <button class="btn btn-secondary me-2" v-on:click="formReading = null; showReadingEditor = false">Avbryt</button>
                                        <button class="btn btn-primary" v-on:click="saveReading">Spara mätvärde</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="mt-3">
                        Visar {{ data.readings.hasMore ? 'de senaste' : '' }} {{ data.readings.length }} avläsningar mellan valda tidpunkter. Senast först.
                    </div>
                    <div class="mt-3">
                        <table class="table">
                            <thead>
                                <tr>
                                    <th>Tidpunkt</th>
                                    <th v-for="field in data.fields">
                                        <div class="text-end">
                                            {{ seriesHelpers.getFieldShortLabel(field) }}
                                        </div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(reading, index) in data.readings" :key="index">
                                    <td>
                                        <span :class="reading.source ? 'walkby-bg' : ''" :title="reading.source === 'walk-by' ? 'Walk-by' : (reading.source === 'manual' ? 'Manuellt inskrivet' : '')">{{ new Date(reading.timestamp).toLocaleString('sv') }}</span>
                                        <button v-if="user.level <= 10" type="button" class="btn btn-link text-body" title="Redigera mätvärde" @click="editReading(reading)">
                                            <i class="bi bi-pencil"></i>
                                        </button>
                                    </td>
                                    <td v-for="field in data.fields">
                                        <div class="text-end" :title="reading.fields[field]">
                                            {{ seriesHelpers.formatFieldValue(field, reading.fields[field]) }} {{ seriesHelpers.getFieldUnit(field) }}
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" @click="$emit('close')">Stäng</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue'
import seriesHelpers from '@/helpers/seriesHelpers'
import VueDatePicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import readingsService from '@/services/readingsService'
import tokenService from '@/services/tokenService'

const emit = defineEmits([ 'close', 'update', 'change-apartment', 'reload' ])

const props = defineProps({
    data: Object,
    apartments: Array,
})

const showReadingEditor = ref(false)
const formReading = ref(null)

const user = tokenService.getUser()

function setTimestamp(_event) {
    document.getElementById('timestampInput').value = new Date().toLocaleString('sv', { hour12: false })
}

function editReading(reading) {
    formReading.value = reading
    showReadingEditor.value = true
}

function saveReading() {
    const timestampStr = document.getElementById('timestampInput').value.trim()
    if (!timestampStr) {
        alert('Ange en tidpunkt för mätvärdet.')
        return
    }
    const timestamp = new Date(timestampStr)
    if (isNaN(timestamp.getTime())) {
        alert('Ogiltigt format för tidpunkt, se info.')
        return
    }

    const fields = {}
    for (const field of props.data.mpFields) {
        const valueStr = document.getElementById(field + 'FieldInput').value.trim()
        if (valueStr) {
            const value = parseFloat(valueStr.replace(',', '.'))
            if (isNaN(value)) {
                alert('Ogiltigt format för fältet ' + seriesHelpers.getFieldShortLabel(field) + ', se info.')
                return
            }
            fields[field] = value
        }
    }

    if (Object.keys(fields).length === 0) {
        alert('Ange minst ett mätvärde.')
        return
    }

    if (formReading.value) {
        readingsService.updateDeviceReading(props.data.deviceId, formReading.value.timestamp, fields, 'manual')
            .then(() => {
                formReading.value = null
                showReadingEditor.value = false
                emit('reload')
                emit('update')
            })
            .catch((error) => {
                alert('Kunde inte uppdatera mätvärde: ' + error)
            })
    } else {
        readingsService.insertDeviceReading(props.data.deviceId, timestamp, fields, 'manual')
            .then(() => {
                formReading.value = null
                showReadingEditor.value = false
                emit('reload')
                emit('update')
            })
            .catch((error) => {
                alert('Kunde inte infoga mätvärde: ' + error)
            })
    }
}

function removeReading() {
    if (!formReading.value) {
        return
    }
    if (!confirm('Är du säker på att du vill ta bort detta mätvärde?')) {
        return
    }

    readingsService.deleteDeviceReading(props.data.deviceId, new Date(formReading.value.timestamp).toISOString())
        .then(() => {
            formReading.value = null
            showReadingEditor.value = false
            emit('reload')
            emit('update')
        })
        .catch((error) => {
            alert('Kunde inte ta bort mätvärde: ' + error)
        })
}

async function download() {
    if (!props.data.deviceId) {
        return
    }

    const data = (await readingsService.getDeviceReadings(
        props.data.deviceId, 
        props.data.start, 
        props.data.stop,
        true, // No limit
    )).data

    let columns = {}
    let columnIndex = 0
    let header = 'timestamp'
    const telegrams = []
    for (const timestamp in data) {
        const telegram = { 0: timestamp }
        telegrams.push(telegram)
        for (const field in data[timestamp]) {
            if (!(field in columns)) {
                columns[field] = ++columnIndex
                header += ';' + field
            }
            telegram[columns[field]] = data[timestamp][field]
        }
    }

    telegrams.sort((a, b) => a[0] < b[0] ? -1 : 1)

    const lines = [ header ]
    for (const telegram of telegrams) {
        const timestamp = new Date(telegram[0])
        let line = `${timestamp.toLocaleDateString('sv')} ${timestamp.toLocaleTimeString('sv')}`
        for (let i = 1; i <= columnIndex; i++) {
            line += ';'
            if (typeof telegram[i] !== 'undefined') {
                line += telegram[i]
            }
        }
        lines.push(line)
    }

    const device = props.data.devices.find(d => d.deviceId == props.data.deviceId)
    let deviceNumber = ''
    if (device) {
        deviceNumber = device.idNumber
    }

    const blob = new Blob([lines.join('\r\n')])
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `Readings ${deviceNumber} ${props.data.start.toLocaleDateString('sv')}--${props.data.stop.toLocaleDateString('sv')}.csv`
    link.click()
    URL.revokeObjectURL(link.href)
}

</script>